import React, { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '../../../shared/i18n';
import { formatSessionTime } from '../../../shared/utils/formatDateTime';
import {
  useMonitoredActivitySessions,
  useApproveMonitoredActivitySession,
  useRejectMonitoredActivitySession,
  useMonitoredActivitySession,
} from '../hooks/useMonitoredActivities';

const TAB_STATUSES = ['completed', 'approved', 'rejected', 'cancelled'];

const TabIcon = ({ status }) => {
  // Small SVG glyphs so the tabs are recognizable at a glance across locales.
  const props = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 14,
    height: 14,
    fill: 'currentColor',
    'aria-hidden': true,
    style: { verticalAlign: '-2px' },
  };
  switch (status) {
    case 'completed':
      // Hourglass — "done recording, awaiting your review" — distinct from the
      // approved check-mark so the two states don't look alike at a glance.
      return (
        <svg {...props} viewBox="0 0 384 512">
          <path d="M0 32C0 14.3 14.3 0 32 0H64 320h32c17.7 0 32 14.3 32 32s-14.3 32-32 32V75c0 42.4-16.9 83.1-46.9 113.1L237.3 256l67.9 67.9c30 30 46.9 70.7 46.9 113.1v11c17.7 0 32 14.3 32 32s-14.3 32-32 32H320 64 32c-17.7 0-32-14.3-32-32s14.3-32 32-32V437c0-42.4 16.9-83.1 46.9-113.1L146.7 256 78.9 188.1C48.9 158.1 32 117.4 32 75V64C14.3 64 0 49.7 0 32zM96 64V75c0 25.5 10.1 49.9 28.1 67.9L192 210.7l67.9-67.9c18-18 28.1-42.4 28.1-67.9V64H96zm0 384H288V437c0-25.5-10.1-49.9-28.1-67.9L192 301.3l-67.9 67.9C106.1 387.2 96 411.6 96 437v11z"/>
        </svg>
      );
    case 'approved':
      return (
        <svg {...props} viewBox="0 0 448 512">
          <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
        </svg>
      );
    case 'rejected':
      return (
        <svg {...props} viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
        </svg>
      );
    case 'cancelled':
      return (
        <svg {...props} viewBox="0 0 512 512">
          <path d="M367.2 412.5C335.9 434.9 297.5 448 256 448C150 448 64 362 64 256c0-41.5 13.1-79.9 35.5-111.2L367.2 412.5zM412.5 367.2L144.8 99.5C176.1 77.1 214.5 64 256 64c106 0 192 86 192 192c0 41.5-13.1 79.9-35.5 111.2zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256z"/>
        </svg>
      );
    default:
      return null;
  }
};

const StatusBadge = ({ status, t }) => {
  const cls =
    status === 'approved' ? 'bg-success' :
    status === 'rejected' ? 'bg-danger' :
    status === 'cancelled' ? 'bg-secondary' :
    'bg-warning text-dark';
  return <span className={`badge ${cls}`}>{t(`guardian.monitored_sessions.status.${status}`)}</span>;
};

const SessionDetail = ({ sessionId, onClose, t }) => {
  const { data: session, isLoading } = useMonitoredActivitySession(sessionId);
  const approveMut = useApproveMonitoredActivitySession();
  const rejectMut = useRejectMonitoredActivitySession();

  if (isLoading || !session) return null;

  const handleApprove = async () => {
    await approveMut.mutateAsync(session.id);
    onClose();
  };
  const handleReject = async () => {
    await rejectMut.mutateAsync(session.id);
    onClose();
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="session-detail-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{session.monitored_activity?.name}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <StatusBadge status={session.status} t={t} />
            </div>
            <p>{t('guardian.monitored_sessions.child')}: <strong>{session.child?.username}</strong></p>
            <p>{t('guardian.monitored_sessions.clip_count')}: {session.clips?.length || 0}</p>
            <div className="row g-2">
              {(session.clips || []).map((c) => (
                <div key={c.id} className="col-6 col-md-4" data-testid={`session-clip-${c.clip_index}`}>
                  <div className="border p-2">
                    <div className="small text-muted">
                      {t('guardian.monitored_sessions.clip_at', { seconds: c.planned_start_seconds })}
                    </div>
                    {c.file_url ? (
                      <video src={c.file_url} controls style={{ width: '100%' }} />
                    ) : (
                      <div className="text-muted small">{t('guardian.monitored_sessions.no_clip')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            {session.status === 'completed' && (
              <>
                <button type="button" className="btn btn-success" onClick={handleApprove} data-testid="session-approve">
                  {t('guardian.monitored_sessions.approve')}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleReject} data-testid="session-reject">
                  {t('guardian.monitored_sessions.reject')}
                </button>
              </>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('guardian.monitored_sessions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MonitoredSessionsPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('completed');
  const [selectedId, setSelectedId] = useState(null);
  const { data, isLoading, error } = useMonitoredActivitySessions(status);

  const sessions = data?.monitored_activity_sessions || [];

  return (
    <div className="container py-4" data-testid="monitored-sessions-page">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">{t('guardian.monitored_sessions.title')}</h2>
        <Link
          to="/monitored-activities"
          className="btn btn-outline-primary"
          data-testid="monitored-activities-settings"
        >
          {t('guardian.monitored_sessions.configure_activities')}
        </Link>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TAB_STATUSES.map((s) => (
          <li key={s} className="nav-item">
            <button
              type="button"
              className={`nav-link d-inline-flex align-items-center gap-2 ${status === s ? 'active' : ''}`}
              onClick={() => setStatus(s)}
              data-testid={`session-tab-${s}`}
            >
              <TabIcon status={s} />
              {t(`guardian.monitored_sessions.status.${s}`)}
            </button>
          </li>
        ))}
      </ul>

      {isLoading && <div className="text-center py-4"><div className="spinner-border" role="status" /></div>}
      {error && <div className="alert alert-danger">{error.message}</div>}

      {!isLoading && sessions.length === 0 && (
        <div className="alert alert-info">{t('guardian.monitored_sessions.empty')}</div>
      )}

      <ul className="list-group">
        {sessions.map((s) => (
          <li key={s.id} className="list-group-item d-flex align-items-center justify-content-between" data-testid={`monitored-session-${s.id}`}>
            <div>
              <div className="fw-bold">{s.monitored_activity?.name}</div>
              <div className="text-muted small">
                {s.child?.username} · {formatSessionTime(s.started_at)} ·
                {' '}{t('guardian.monitored_sessions.clip_count')}: {s.clip_count}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <StatusBadge status={s.status} t={t} />
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setSelectedId(s.id)}
                data-testid={`session-review-${s.id}`}
              >
                {t('guardian.monitored_sessions.review')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {selectedId && <SessionDetail sessionId={selectedId} onClose={() => setSelectedId(null)} t={t} />}
    </div>
  );
};

export default MonitoredSessionsPage;
